import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { claimConflictSchema, conversationResponseSchema } from '@shared/api';
import type { ConversationDetail, ParkedRow } from '@shared/types';
import { api, HTTPError } from '@web/lib/api';
import { flushBoard } from '@web/lib/boardBuffer';
import { toast } from '@web/hooks/useToast';
import { keys } from '@web/lib/keys';

/**
 * First-wins claim, optimistically.
 *
 * The one nuance that makes this clean: on a 409 do NOT roll the optimistic
 * removal back. The row really is gone — a colleague owns it — and restoring it
 * would flash a dead row that the incoming broadcast deletes again a moment
 * later. Roll back only for a genuine failure, where the conversation is still
 * waiting and somebody has to pick it up.
 */
export function useClaim(): UseMutationResult<ConversationDetail, Error, string> {
  const client = useQueryClient();
  const navigate = useNavigate();

  return useMutation<ConversationDetail, Error, string, { previous: ParkedRow[] | undefined }>({
    mutationFn: async (conversationId) => {
      const response = await api.post(
        `/api/conversations/${conversationId}/claim`,
        conversationResponseSchema,
      );
      return response.conversation;
    },

    onMutate: async (conversationId) => {
      // A refetch already in flight would otherwise land after this write and
      // put the row back.
      await client.cancelQueries({ queryKey: keys.parked });
      const previous = client.getQueryData<ParkedRow[]>(keys.parked);
      client.setQueryData<ParkedRow[]>(keys.parked, (old) =>
        old?.filter((row) => row.conversationId !== conversationId),
      );
      return { previous };
    },

    onSuccess: (conversation) => {
      client.setQueryData(keys.conversation(conversation.id), conversation);
      // The board should already agree with the thread the operator is about to
      // land on, rather than catching up 200ms later.
      flushBoard(client);
      void navigate(`/thread/${conversation.id}`);
    },

    onError: (error, _conversationId, context) => {
      if (error instanceof HTTPError && error.status === 409) {
        const conflict = claimConflictSchema.safeParse(error.body);
        const winner = conflict.success ? conflict.data.claimedBy?.name : undefined;
        toast(
          'warn',
          `${winner ?? 'A colleague'} claimed it first`,
          'The conversation has left your queue.',
        );
        return; // keep the row removed — it is genuinely gone
      }
      if (context?.previous !== undefined) {
        client.setQueryData(keys.parked, context.previous);
      }
      toast('error', 'Claim failed', 'Check your connection and try again.');
    },

    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.parked });
    },
  });
}
